import { Component, computed, ElementRef, HostListener, inject, ViewChild, NgZone, afterNextRender, OnDestroy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '@env/environment';
import { DevBottomBarService } from '../dev-bottom-bar.service';
import { NetworkPanelComponent } from '../network/network-panel.component';
import { DevConsolePanelComponent } from '../dev-console/dev-console-panel.component';

@Component({
  selector: 'app-dev-bottom-bar',
  standalone: true,
  imports: [AsyncPipe, MatIconModule, NetworkPanelComponent, DevConsolePanelComponent],
  templateUrl: './dev-bottom-bar.component.html',
  styleUrls: ['./dev-bottom-bar.component.css'],
  host: {
    'popover': 'manual',
    '[class.minimized]': 'isMinimized()',
    '[class.has-bar]': 'anyVisible()',
    '[style.height.px]': 'heightPx()',
    // CSS custom properties consumed by the row fade animations.
    '[style.--dev-bar-net-fade-ms]': 'netFadeMsCss()',
    '[style.--dev-bar-console-fade-ms]': 'consoleFadeMsCss()',
  },
})
export class DevBottomBarComponent implements OnDestroy {
  readonly service = inject(DevBottomBarService);
  private readonly el = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  readonly isProduction = environment.production;
  readonly isMinimized = toSignal(this.service.minimized$, { requireSync: true });
  readonly anyVisible = toSignal(this.service.anyVisible$, { initialValue: false });
  readonly heightPx = toSignal(this.service.heightPx$, { requireSync: true });

  private readonly netFadeMs = toSignal(this.service.fadeDurationNetworkMs$, { requireSync: true });
  private readonly consoleFadeMs = toSignal(this.service.fadeDurationConsoleMs$, { requireSync: true });

  readonly netFadeMsCss = computed(() => `${this.netFadeMs()}ms`);
  readonly consoleFadeMsCss = computed(() => `${this.consoleFadeMs()}ms`);

  private overlayObserver: MutationObserver | null = null;
  private bodyObserver: MutationObserver | null = null;

  /** Dynamic layering vs CDK overlays: true while the pointer hovers a modal,
   *  meaning the bar has stepped out of the browser's top layer so the modal
   *  renders above it. Hovering the bar (or its screen area) flips it back. */
  private consoleBehind = false;

  constructor() {
    // The host element uses popover="manual" so it lives in the browser's top
    // layer — the same mechanism CDK v22 uses for dialogs. We show it once and
    // then re-show whenever the CDK adds a new overlay (which pushes the new
    // overlay to the top of the layer stack). Re-showing moves us back on top.
    afterNextRender(() => {
      const host = this.el.nativeElement as HTMLElement;
      host.showPopover();

      this.ngZone.runOutsideAngular(() => {
        document.addEventListener('mousemove', this.onDocumentMouseMove, { passive: true });

        const cdkContainer = document.querySelector('.cdk-overlay-container');
        if (cdkContainer) {
          this.watchCdkContainer(host, cdkContainer);
        } else {
          // CDK creates the container lazily — watch body until it appears.
          this.bodyObserver = new MutationObserver(() => {
            const container = document.querySelector('.cdk-overlay-container');
            if (container) {
              this.bodyObserver!.disconnect();
              this.bodyObserver = null;
              this.watchCdkContainer(host, container);
            }
          });
          this.bodyObserver.observe(document.body, { childList: true });
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.overlayObserver?.disconnect();
    this.bodyObserver?.disconnect();
    document.removeEventListener('mousemove', this.onDocumentMouseMove);
    try {
      (this.el.nativeElement as HTMLElement).hidePopover();
    } catch { /* not shown */ }
  }

  /** Watch the CDK overlay container for new overlays and re-show our popover
   *  to keep it at the top of the browser's top layer stack. */
  private watchCdkContainer(host: HTMLElement, container: Element): void {
    this.overlayObserver = new MutationObserver(() => {
      // Defer so it runs AFTER the CDK has called showPopover() on the new overlay.
      setTimeout(() => {
        if (this.consoleBehind) {
          // The pointer is holding a modal in front — don't fight it. But if
          // the last overlay just closed, restore the default (bar on top).
          if (container.childElementCount === 0) this.setConsoleBehind(false);
          return;
        }
        try {
          host.hidePopover();
          host.showPopover();
        } catch { /* already hidden or not shown */ }
      });
    });
    this.overlayObserver.observe(container, { childList: true });
  }

  /** Document-level pointer tracking (delegation — overlay panes are created
   *  dynamically, so per-pane listeners would not scale). Decides which layer
   *  should be on top:
   *  - pointer inside the bar            → bar on top (default)
   *  - pointer on an overlay backdrop    → modal on top, EXCEPT over the strip
   *    of screen the bar occupies (backdrops cover the whole viewport, so when
   *    the bar is behind, the backdrop is what actually receives the event
   *    there — treat that strip as "hovering the console")
   *  - pointer on any overlay pane       → modal on top
   *  - anywhere else                     → bar on top (default)
   */
  private readonly onDocumentMouseMove = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const host = this.el.nativeElement as HTMLElement;

    if (host.contains(target)) {
      this.setConsoleBehind(false);
      return;
    }

    if (target.closest('.cdk-overlay-backdrop')) {
      const r = host.getBoundingClientRect();
      const overBar =
        r.width > 0 &&
        event.clientX >= r.left && event.clientX <= r.right &&
        event.clientY >= r.top && event.clientY <= r.bottom;
      this.setConsoleBehind(!overBar);
      return;
    }

    if (target.closest('.cdk-overlay-popover, .cdk-overlay-container')) {
      this.setConsoleBehind(true);
      return;
    }

    this.setConsoleBehind(false);
  };

  /** Swap the layering. Elements in the browser's top layer stack in the order
   *  they were shown and ignore z-index, so the only way to drop below the CDK
   *  overlays is to leave the top layer entirely: hidePopover() + a CSS
   *  override (`.console-behind`) that keeps the closed popover visible in its
   *  normal fixed position. showPopover() re-enters the top layer on top. */
  private setConsoleBehind(behind: boolean): void {
    if (this.consoleBehind === behind) return;
    this.consoleBehind = behind;
    const host = this.el.nativeElement as HTMLElement;
    host.classList.toggle('console-behind', behind);
    try {
      if (behind) {
        if (host.matches(':popover-open')) host.hidePopover();
      } else if (!host.matches(':popover-open')) {
        host.showPopover();
      }
    } catch { /* popover state changed underneath us — next event resyncs */ }
  }

  /** When a HUD fade animation finishes, collapse the row so the remaining
   *  visible stack slides down instead of leaving an invisible gap. The class
   *  only takes effect under `:host.minimized` so expanding restores them. */
  @HostListener('animationend', ['$event'])
  onHudAnimationEnd(event: AnimationEvent): void {
    if (event.animationName === 'devBarFadeOut' || event.animationName === 'devBarFadeOutError') {
      (event.target as HTMLElement).classList.add('dev-hud-faded');
    }
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(e: KeyboardEvent): void {
    if (!(e.ctrlKey || e.metaKey) || e.key !== 'f') return;
    if (this.isMinimized() || !this.anyVisible()) return;

    const host = this.el.nativeElement as HTMLElement;

    // 1) Check if a maximized JSON viewer is open — focus its search
    const jvSearch = host.querySelector('.jv-maximized .jv-search-input') as HTMLInputElement;
    if (jvSearch) {
      e.preventDefault();
      // If search bar is not visible, click the search toggle button first
      if (!jvSearch.offsetParent) {
        const searchBtn = host.querySelector('.jv-maximized .jv-tool-active, .jv-maximized [title*="Search"]') as HTMLElement;
        searchBtn?.click();
        setTimeout(() => {
          const inp = host.querySelector('.jv-maximized .jv-search-input') as HTMLInputElement;
          inp?.focus();
        });
      } else {
        jvSearch.focus();
      }
      return;
    }

    // 2) Focus the appropriate search input inside the panels
    // Try network panel first (if visible)
    const networkPanel = host.querySelector('app-dev-network-panel') as HTMLElement;
    const consolePanel = host.querySelector('app-dev-console-panel') as HTMLElement;

    if (networkPanel) {
      e.preventDefault();
      // Check if filter row is collapsed when console is also active (tune button mode)
      const tuneBtn = networkPanel.querySelector('.header-btn[title="Toggle filters"]:not(.active)') as HTMLElement;
      if (tuneBtn) tuneBtn.click();
      // Wait a tick for the filter row to appear, then focus the search input
      setTimeout(() => {
        const searchInput = networkPanel.querySelector('.search-input, .inline-filters .search-input') as HTMLInputElement;
        if (searchInput) searchInput.focus();
      });
      return;
    }

    if (consolePanel) {
      e.preventDefault();
      const searchInput = consolePanel.querySelector('.search-input') as HTMLInputElement;
      if (searchInput) searchInput.focus();
      return;
    }
  }

  /** Drag the top edge of the expanded bar to resize its height. Disabled in
   *  minimized mode — the HUD overlay has no business being resizable. */
  startResize(event: MouseEvent): void {
    if (this.isMinimized()) return;
    event.preventDefault();
    event.stopPropagation();
    const startY = event.clientY;
    const startHeight = this.service.heightPx$.value;

    const onMove = (e: MouseEvent) => {
      // Dragging up (ΔY negative) should grow the bar since it's anchored at
      // the bottom of the viewport.
      this.service.setHeightPx(startHeight + (startY - e.clientY));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }
}
